import React, { useState, useEffect } from 'react';
import axios from 'axios';
import SidebarLayout from './SidebarLayout';
import './shopStyles.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.ogestyle.com';

// Helper to format dates for display (handles both Date objects and YYYY-MM-DD strings)
const formatDisplayDate = (dateValue) => {
  if (!dateValue) return '';
  
  // If it's already a string in YYYY-MM-DD format, convert to MM/DD/YYYY
  if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
    const [year, month, day] = dateValue.split('-');
    return `${month}/${day}/${year}`;
  }
  
  // If it's a Date object or date string, parse and format
  const date = new Date(dateValue);
  if (isNaN(date.getTime())) return dateValue; // Return as-is if invalid
  
  // Add time to avoid timezone offset
  const adjusted = new Date(date.getTime() + Math.abs(date.getTimezoneOffset() * 60000));
  return `${String(adjusted.getMonth() + 1).padStart(2, '0')}/${String(adjusted.getDate()).padStart(2, '0')}/${adjusted.getFullYear()}`;
};

const BusinessOperations = () => {
  const [loading, setLoading] = useState(true);
  const [businessId, setBusinessId] = useState(null);
  const [blockedDates, setBlockedDates] = useState([]);
  const [showBlockedDateModal, setShowBlockedDateModal] = useState(false);
  const [editingBlockedDate, setEditingBlockedDate] = useState(null);
  const [blockedDateForm, setBlockedDateForm] = useState({
    startDate: '',
    endDate: '',
    reason: ''
  });
  const [alert, setAlert] = useState({ show: false, message: '', type: '' });
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchBusinessData = React.useCallback(async () => {
    try {
      const token = localStorage.getItem('ogestyle_token');
      let busId = null;
      
      // Try staff profile first (for R1 owner-professionals and R2 shop admins)
      try {
        const staffRes = await axios.get(`${API_BASE_URL}/api/staff/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const staffData = staffRes.data.staff || staffRes.data;
        
        if (staffData) {
          busId = typeof staffData.businessId === 'object' 
            ? staffData.businessId?._id 
            : staffData.businessId;
        }
      } catch (error) {
      }
      
      // Fallback: get from shops endpoint
      if (!busId) {
        const shopsRes = await axios.get(`${API_BASE_URL}/api/shop/my-shops`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const shops = Array.isArray(shopsRes.data) ? shopsRes.data : (shopsRes.data.shops || []);
        
        if (shops.length > 0) {
          busId = shops[0].businessId?._id || shops[0].businessId;
        }
      }
      
      if (!busId) {
        showAlert('No business associated with your account', 'error');
        setLoading(false);
        return;
      }
      
      setBusinessId(busId);
    } catch (error) {
      showAlert('Failed to load business data', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBusinessData();
  }, [fetchBusinessData]);

  const fetchBlockedDates = React.useCallback(async () => {
    if (!businessId) return;
    try {
      const token = localStorage.getItem('ogestyle_token');
      const response = await axios.get(
        `${API_BASE_URL}/api/business/${businessId}/blocked-dates`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBlockedDates(response.data.blockedDates || []);
    } catch (error) {
      if (error.response?.status !== 404) {
        showAlert('Failed to load blocked dates', 'error');
      }
    }
  }, [businessId]);

  useEffect(() => {
    fetchBlockedDates();
  }, [fetchBlockedDates]);

  const handleSaveBlockedDate = async () => {
    try {
      const token = localStorage.getItem('ogestyle_token');
      
      if (editingBlockedDate) {
        await axios.put(
          `${API_BASE_URL}/api/business/${businessId}/blocked-dates/${editingBlockedDate._id}`,
          blockedDateForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        showAlert('Blocked date updated successfully', 'success');
      } else {
        await axios.post(
          `${API_BASE_URL}/api/business/${businessId}/blocked-dates`,
          blockedDateForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        showAlert('Blocked date added successfully', 'success');
      }
      
      closeBlockedDateModal();
      fetchBlockedDates();
    } catch (error) {
      showAlert('Failed to save blocked date', 'error');
    }
  };

  const handleDeleteBlockedDate = async (dateId) => {
    if (!window.confirm('Are you sure you want to delete this blocked date? This will affect all locations.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('ogestyle_token');
      await axios.delete(
        `${API_BASE_URL}/api/business/${businessId}/blocked-dates/${dateId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showAlert('Blocked date deleted successfully', 'success');
      fetchBlockedDates();
    } catch (error) {
      showAlert('Failed to delete blocked date', 'error');
    }
  };

  const openAddBlockedDateModal = () => {
    setEditingBlockedDate(null);
    setBlockedDateForm({ startDate: '', endDate: '', reason: '' });
    setShowBlockedDateModal(true);
  };

  const openEditBlockedDateModal = (date) => {
    setEditingBlockedDate(date);
    setBlockedDateForm({
      startDate: date.startDate.split('T')[0],
      endDate: date.endDate.split('T')[0],
      reason: date.reason
    });
    setShowBlockedDateModal(true);
  };

  const closeBlockedDateModal = () => {
    setShowBlockedDateModal(false);
    setEditingBlockedDate(null);
    setBlockedDateForm({ startDate: '', endDate: '', reason: '' });
  };

  const showAlert = (message, type) => {
    setAlert({ show: true, message, type });
    setTimeout(() => setAlert({ show: false, message: '', type: '' }), 3000);
  };

  if (loading) {
    return (
      <SidebarLayout>
        <div className="bookings-container settings-page">
          <div className="loading-spinner">Loading business operations...</div>
        </div>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout>
      <div className="bookings-container settings-page">
        {/* Alert */}
        {alert.show && (
          <div className={`alert alert-${alert.type}`}>
            {alert.message}
          </div>
        )}

        {/* Header */}
        <div className="bookings-header">
          <h1>Business Operations</h1>
          <p style={{ color: '#6b7280', marginTop: '8px' }}>
            Manage company-wide blocked dates and holidays
          </p>
        </div>

        {/* Blocked Dates Section */}
        <div className="settings-section">
          <div style={{ marginBottom: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
              <div>
                <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', margin: 0 }}>Business-Wide Blocked Dates</h3>
              </div>
              <button
                className="btn btn-primary"
                onClick={openAddBlockedDateModal}
                style={{ fontSize: isMobile ? '14px' : '16px' }}
              >
                + Add Blocked Date
              </button>
            </div>
            <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '16px' }}>
              Block dates across <strong>all locations</strong> in your business. These dates will be unavailable for bookings from <strong>all professionals</strong>.
            </p>

            {blockedDates.length === 0 ? (
              <div style={{ padding: '40px 20px', textAlign: 'center', background: '#f9fafb', borderRadius: '8px', border: '1px dashed #d1d5db' }}>
                <p style={{ color: '#6b7280', margin: 0 }}>No blocked dates set. Add company holidays or closure periods.</p>
              </div>
            ) : (
              <div style={{ display: 'grid', gap: '12px' }}>
                {blockedDates.map((date) => (
                  <div
                    key={date._id}
                    style={{
                      padding: '16px',
                      background: '#fff',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: '12px'
                    }}
                  >
                    <div style={{ flex: 1, minWidth: '200px' }}>
                      <div style={{ fontWeight: '600', color: '#111827', marginBottom: '4px' }}>{date.reason}</div>
                      <div style={{ fontSize: '14px', color: '#6b7280' }}>
                        {formatDisplayDate(date.startDate)} - {formatDisplayDate(date.endDate)}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => openEditBlockedDateModal(date)}
                        className="btn btn-secondary"
                        style={{ padding: '8px 16px', fontSize: '14px' }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteBlockedDate(date._id)}
                        style={{
                          padding: '8px 16px',
                          background: '#ef4444',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '14px'
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Blocked Date Modal */}
        {showBlockedDateModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}>
            <div style={{
              background: '#fff',
              borderRadius: '12px',
              padding: isMobile ? '24px' : '32px',
              maxWidth: '500px',
              width: '100%',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}>
              <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '8px' }}>
                {editingBlockedDate ? 'Edit Business-Wide Blocked Date' : 'Add Business-Wide Blocked Date'}
              </h3>
              <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '24px' }}>
                This will affect all locations and all professionals' calendars across your entire business
              </p>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                  Start Date
                </label>
                <input
                  type="date"
                  value={blockedDateForm.startDate}
                  onChange={(e) => setBlockedDateForm({ ...blockedDateForm, startDate: e.target.value })}
                  className="input-field"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                  End Date
                </label>
                <input
                  type="date"
                  value={blockedDateForm.endDate}
                  onChange={(e) => setBlockedDateForm({ ...blockedDateForm, endDate: e.target.value })}
                  className="input-field"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                  Reason (e.g., Holiday, Renovation, etc.)
                </label>
                <input
                  type="text"
                  value={blockedDateForm.reason}
                  onChange={(e) => setBlockedDateForm({ ...blockedDateForm, reason: e.target.value })}
                  placeholder="Enter reason for blocking dates"
                  className="input-field"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={closeBlockedDateModal}
                  className="btn btn-secondary"
                  style={{ padding: '10px 20px' }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveBlockedDate}
                  className="btn btn-primary"
                  style={{ padding: '10px 20px' }}
                  disabled={!blockedDateForm.startDate || !blockedDateForm.endDate || !blockedDateForm.reason}
                >
                  {editingBlockedDate ? 'Update' : 'Add'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </SidebarLayout>
  );
};

export default BusinessOperations;
